import React, { useEffect, useState, useTransition } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import Rating3Badge from "../common/Rating3Badge";
import EloBadge from "../common/EloBadge.jsx";
import utils from "../../universal/utils";
import { dateLinesFromMatch } from "./profileRecentMatches";
import ProfileTableShowMoreRow from "./ProfileTableShowMoreRow";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const edgeBasePath = utils.getEdgeBasePath();
const MATCHES_PAGE_SIZE = 30;

const MATCH_TABLE_COLUMNS_WITH_ADR =
    "minmax(5.5rem, 1.1fr) minmax(6rem, 1fr) minmax(5rem, 0.85fr) minmax(4.5rem, 0.7fr) minmax(5rem, 0.75fr) minmax(3.5rem, 0.5fr)";

const MATCH_TABLE_COLUMNS_NO_ADR =
    "minmax(5.5rem, 1.1fr) minmax(6rem, 1fr) minmax(5rem, 0.85fr) minmax(4.5rem, 0.7fr) minmax(5rem, 0.75fr)";

function getMatchTableColumns(showAdr) {
    return showAdr ? MATCH_TABLE_COLUMNS_WITH_ADR : MATCH_TABLE_COLUMNS_NO_ADR;
}

const MATCH_TABLE_HEADER_SX = {
    display: "grid",
    gap: "0.75rem",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "0.575rem 1.15rem",
    minHeight: "3rem",
    borderRadius: "10px",
    background: `
        linear-gradient(
            180deg,
            rgba(16,18,20,0.96) 0%,
            rgba(10,12,14,0.98) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.04)",
    backdropFilter: "blur(16px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        0 10px 30px rgba(0,0,0,0.28)
    `,
    position: "relative",
    overflow: "hidden",
};

const MATCH_TABLE_ROW_GRID_SX = {
    display: "grid",
    gap: "0.75rem",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    px: "1.15rem",
    py: "0.65rem",
};

const HEADER_CELL_SX = {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.42)",
    lineHeight: 1,
    m: 0,
};

function MatchTableHeader({ showAdr = true }) {
    const columns = [
        { label: "Map", align: "flex-start" },
        { label: "Date", align: "flex-start" },
        { label: "Score", align: "center" },
        { label: "Rating 3.0", align: "center" },
        { label: "K/D/A", align: "center" },
        ...(showAdr ? [{ label: "ADR", align: "center" }] : []),
    ];

    return (
        <Box
            sx={{
                ...MATCH_TABLE_HEADER_SX,
                gridTemplateColumns: getMatchTableColumns(showAdr),
                display: { xs: "none", md: "grid" },
            }}
        >
            {columns.map(({ label, align }) => (
                <Box
                    key={label}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: align,
                        minWidth: 0,
                    }}
                >
                    <Typography component="span" sx={HEADER_CELL_SX}>
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

MatchTableHeader.propTypes = {
    showAdr: PropTypes.bool,
};

function ResultBadge({ win, accent, size = "desktop" }) {
    const isMobile = size === "mobile";
    return (
        <Box
            sx={{
                minWidth: isMobile ? "1.65rem" : "1.5rem",
                height: isMobile ? "1.65rem" : "1.5rem",
                borderRadius: isMobile ? "7px" : "6px",
                bgcolor: accent,
                boxShadow: `0 0 14px ${alpha(accent, 0.32)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Typography
                sx={{
                    color: colors.text.primary,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    fontSize: isMobile ? "0.72rem" : "0.64rem",
                }}
            >
                {win ? "W" : "L"}
            </Typography>
        </Box>
    );
}

ResultBadge.propTypes = {
    win: PropTypes.bool.isRequired,
    accent: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["desktop", "mobile"]),
};

function MatchScoreText({ myS, oppS, myScoreColor, fontSize = "0.92rem" }) {
    return (
        <Typography
            component="span"
            sx={{
                fontSize,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                fontVariantNumeric: "tabular-nums",
            }}
        >
            <Box component="span" sx={{ color: myScoreColor, fontWeight: 700 }}>
                {myS}
            </Box>
            <Box component="span" sx={{ color: colors.text.secondary }}>
                {" "}
                :{" "}
            </Box>
            <Box
                component="span"
                sx={{ color: colors.text.secondary, fontWeight: 500 }}
            >
                {oppS}
            </Box>
        </Typography>
    );
}

MatchScoreText.propTypes = {
    myS: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    oppS: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    myScoreColor: PropTypes.string.isRequired,
    fontSize: PropTypes.string,
};

function MapThumb({ mapImageUrl, mapLabel, size = "2.25rem" }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: "10px",
                overflow: "hidden",
                flexShrink: 0,
                bgcolor: alpha(colors.background.surface, 0.9),
                border: `1px solid ${colors.border.default}`,
                position: "relative",
                boxShadow: "0 6px 18px rgba(0,0,0,0.32)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        linear-gradient(
                            180deg,
                            transparent 20%,
                            rgba(0,0,0,0.38) 100%
                        )
                    `,
                    pointerEvents: "none",
                    zIndex: 1,
                }}
            />
            {mapImageUrl ? (
                <Box
                    component="img"
                    src={mapImageUrl}
                    alt={mapLabel}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : null}
        </Box>
    );
}

MapThumb.propTypes = {
    mapImageUrl: PropTypes.string,
    mapLabel: PropTypes.string.isRequired,
    size: PropTypes.string,
};

function EloDelta({ eloChange }) {
    const delta = Number(eloChange);
    if (!Number.isFinite(delta) || delta === 0) {
        return null;
    }
    const positive = delta > 0;
    const color = positive ? colors.state.success.main : colors.state.danger.main;
    const Arrow = positive ? ArrowUpward : ArrowDownward;

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.15rem",
                color,
                flexShrink: 0,
            }}
        >
            <Arrow sx={{ fontSize: "0.95rem" }} />
            <Typography
                component="span"
                sx={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                }}
            >
                {Math.abs(Math.round(delta))}
            </Typography>
        </Box>
    );
}

EloDelta.propTypes = {
    eloChange: PropTypes.number,
};

function MatchTableRow({ match, index, showAdr = true }) {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const win = match.result === "win";
    const accent = win ? colors.state.success.main : colors.state.danger.main;
    const my = Number(match.matchScore?.myTeam);
    const opp = Number(match.matchScore?.opponentTeam);
    const myS = Number.isFinite(my) ? my : "—";
    const oppS = Number.isFinite(opp) ? opp : "—";
    const myScoreColor = win ? colors.state.success.main : colors.state.danger.main;
    const { dateLine, timeLine } = dateLinesFromMatch(match);
    const mapMeta = getMapMetaById(match.map);
    const mapImageUrl = getMapImageUrl(match.map, edgeBasePath);
    const mapLabel = mapMeta?.display || match.map || "Unknown";
    const adr = Number(match.adr);
    const eloAfter = Number(match.eloAfter);
    const hasEloAfter = Number.isFinite(eloAfter) && eloAfter > 0;

    const matchId =
        match.matchId != null && String(match.matchId).trim() !== ""
            ? String(match.matchId).trim()
            : null;
    const rowLinkProps = matchId
        ? {
              component: Link,
              to: `/match/${matchId}`,
              "aria-label": "View match",
          }
        : {};

    return (
        <MotionBox
            whileHover={isDesktop ? { y: -2 } : undefined}
            {...rowLinkProps}
            sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(18,20,22,0.88) 0%,
                        rgba(12,14,16,0.94) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.04)",
                backdropFilter: "blur(14px)",
                transition: `
                    transform 260ms cubic-bezier(0.22,1,0.36,1),
                    border-color 260ms cubic-bezier(0.22,1,0.36,1),
                    background 260ms cubic-bezier(0.22,1,0.36,1),
                    box-shadow 260ms cubic-bezier(0.22,1,0.36,1)
                `,
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.03),
                    0 8px 24px rgba(0,0,0,0.22)
                `,
                cursor: matchId ? "pointer" : "default",
                textDecoration: "none",
                color: "inherit",
                "&:hover": {
                    borderColor: alpha(accent, 0.28),
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(22,24,26,0.96),
                            rgba(14,16,18,0.98)
                        )
                    `,
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        0 14px 40px rgba(0,0,0,0.34),
                        0 0 24px ${alpha(accent, 0.08)}
                    `,
                },
            }}
        >
            <Box
                sx={{
                    width: { xs: "5px", md: "4px" },
                    borderRadius: "0 999px 999px 0",
                    boxShadow: `0 0 12px ${alpha(accent, 0.55)}`,
                    flexShrink: 0,
                    bgcolor: accent,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0,
                    transition: "opacity 260ms ease",
                    background: `
                        linear-gradient(
                            90deg,
                            ${alpha(accent, 0.06)},
                            transparent 35%
                        )
                    `,
                    pointerEvents: "none",
                    ".MuiBox-root:hover &": {
                        opacity: 1,
                    },
                }}
            />

            {/* Mobile stacked card */}
            <Box
                sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    gap: "0.7rem",
                    px: "0.95rem",
                    py: "0.95rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.55)",
                        lineHeight: 1.2,
                    }}
                >
                    {dateLine}
                    {timeLine ? ` · ${timeLine}` : ""}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.65rem",
                    }}
                >
                    <ResultBadge win={win} accent={accent} size="mobile" />
                    <MatchScoreText
                        myS={myS}
                        oppS={oppS}
                        myScoreColor={colors.text.primary}
                        fontSize="1.15rem"
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.65rem",
                        minWidth: 0,
                    }}
                >
                    <MapThumb
                        mapImageUrl={mapImageUrl}
                        mapLabel={mapLabel}
                        size="1.85rem"
                    />
                    <Typography
                        sx={{
                            fontSize: "0.88rem",
                            fontWeight: 700,
                            color: colors.text.primary,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                            flex: "1 1 auto",
                        }}
                    >
                        {mapLabel}
                    </Typography>
                    {hasEloAfter ? (
                        <EloBadge value={eloAfter} size="nano" />
                    ) : showAdr && Number.isFinite(adr) && adr > 0 ? (
                        <Typography
                            sx={{
                                fontSize: "0.88rem",
                                fontWeight: 700,
                                color: colors.text.primary,
                                fontVariantNumeric: "tabular-nums",
                                flexShrink: 0,
                            }}
                        >
                            {adr.toFixed(0)} ADR
                        </Typography>
                    ) : null}
                    <EloDelta eloChange={match.eloChange} />
                </Box>

                <Box
                    sx={{
                        height: "1px",
                        width: "100%",
                        bgcolor: "rgba(255,255,255,0.08)",
                        my: "0.1rem",
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                    }}
                >
                    <Rating3Badge value={match.rating3} size="small" />
                    <Typography
                        sx={{
                            fontSize: "0.95rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            color: colors.text.primary,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {match.kills} / {match.deaths} / {match.assists}
                    </Typography>
                </Box>
            </Box>

            {/* Desktop grid row */}
            <Box
                sx={{
                    ...MATCH_TABLE_ROW_GRID_SX,
                    gridTemplateColumns: getMatchTableColumns(showAdr),
                    display: { xs: "none", md: "grid" },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        minWidth: 0,
                    }}
                >
                    <MapThumb mapImageUrl={mapImageUrl} mapLabel={mapLabel} />
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            color: colors.text.primary,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mapLabel}
                    </Typography>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: colors.text.primary,
                            lineHeight: 1.2,
                        }}
                    >
                        {dateLine}
                    </Typography>
                    {timeLine ? (
                        <Typography
                            sx={{
                                mt: "0.15rem",
                                fontSize: "0.78rem",
                                fontWeight: 400,
                                color: colors.text.secondary,
                                lineHeight: 1.2,
                            }}
                        >
                            {timeLine}
                        </Typography>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.55rem",
                        minWidth: 0,
                    }}
                >
                    <ResultBadge win={win} accent={accent} />
                    <MatchScoreText
                        myS={myS}
                        oppS={oppS}
                        myScoreColor={myScoreColor}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Rating3Badge value={match.rating3} size="small" />
                </Box>

                <Typography
                    sx={{
                        fontSize: "0.825rem",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        color: colors.text.primary,
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "center",
                        textShadow: "0 0 10px rgba(255,255,255,0.03)",
                    }}
                >
                    {match.kills} / {match.deaths} / {match.assists}
                </Typography>

                {showAdr ? (
                    <Typography
                        sx={{
                            fontSize: "0.825rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            color: colors.text.primary,
                            lineHeight: 1,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "center",
                            textShadow: "0 0 10px rgba(255,255,255,0.03)",
                        }}
                    >
                        {Number.isFinite(adr) ? adr.toFixed(1) : "—"}
                    </Typography>
                ) : null}
            </Box>
        </MotionBox>
    );
}

MatchTableRow.propTypes = {
    match: PropTypes.object.isRequired,
    index: PropTypes.number,
    showAdr: PropTypes.bool,
};

export default function ProfileMatchesTable({ matches = [], showAdr = true }) {
    const [visibleCount, setVisibleCount] = useState(MATCHES_PAGE_SIZE);
    const [isLoadingMore, startLoadingMore] = useTransition();

    useEffect(() => {
        setVisibleCount(MATCHES_PAGE_SIZE);
    }, [matches]);

    const visibleMatches = matches.slice(0, visibleCount);
    const hasMore = visibleCount < matches.length;

    const handleShowMore = () => {
        startLoadingMore(() => {
            setVisibleCount((count) =>
                Math.min(count + MATCHES_PAGE_SIZE, matches.length)
            );
        });
    };

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: { xs: "0.65rem", md: "0.5rem" },
            }}
        >
            <MatchTableHeader showAdr={showAdr} />
            {visibleMatches.map((match, index) => (
                <MatchTableRow
                    key={`${match.timestamp}-${match.matchId ?? index}`}
                    match={match}
                    index={index}
                    showAdr={showAdr}
                />
            ))}
            {hasMore || isLoadingMore ? (
                <ProfileTableShowMoreRow
                    onClick={handleShowMore}
                    isLoading={isLoadingMore}
                />
            ) : null}
        </Box>
    );
}

ProfileMatchesTable.propTypes = {
    matches: PropTypes.arrayOf(PropTypes.object),
    showAdr: PropTypes.bool,
};

export { MatchTableHeader, MATCH_TABLE_COLUMNS_WITH_ADR as MATCH_TABLE_COLUMNS };
